import React, { useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { Form, message, Tooltip } from 'antd';
import { StyledEmailWrapper, StyledInfo, StyledSelect } from './styled';
import { editSubscriptionAtom } from '../atoms';
import { EditCreateFormProps, EmailSubscription } from '../types';
import { getFlattenedMetricSeverity, validateEmailArray } from '../utils';
import EditMetricCategoryPanels from './EditMetricCategoryPanels';
import { isAdminRoleSelector } from '@src/store/common/selectors';
import { useSelector } from 'react-redux';
import { BaseOptionType } from 'antd/es/select';
import { InfoCircleOutlined } from '@ant-design/icons';

const MAX_COUNT = 10;

const SubscriptionForm = ({
  userEmails,
  form,
  type,
  subscriptionData,
  metricsMeta,
  searchProps,
}: EditCreateFormProps) => {
  const editSubscription = useAtomValue(editSubscriptionAtom);
  const isAdminUser = useSelector(isAdminRoleSelector);
  const emailArrayWatch: string[] = Form.useWatch('emailArray', form) || [];
  const { allESMetricKeys, categoryMap } = metricsMeta;
  const onFinish = () => {
    message.success('Submit success!');
  };

  const onFinishFailed = () => {
    message.error('Submit failed!');
  };

  const getEmailOptions = (): BaseOptionType[] => {
    const uniqueEmails = Array.from(new Set(userEmails));

    return uniqueEmails.map((email) => ({
      label: email,
      value: email,
      disabled: !emailArrayWatch?.includes(email) && emailArrayWatch?.length >= MAX_COUNT,
    }));
  };

  const defaultSubscriptionData: EmailSubscription = {
    email: '',
    hidden: false,
    metrics: [],
  };

  const formData: EmailSubscription =
    type === 'create' ? defaultSubscriptionData : subscriptionData || {};

  useEffect(() => {
    const initialMetricSeverityValues = getFlattenedMetricSeverity({
      subscriptionMetrics: formData.metrics,
      subscriptionMetricsAutoInsights: formData.autoInsights,
      allESMetricKeys,
      type,
    });
    const initialEmailArray = formData.email ? [formData.email] : [];

    form.setFieldsValue({
      emailArray: initialEmailArray,
      metricList: initialMetricSeverityValues,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(formData)]);

  return (
    <Form
      key={type}
      form={form}
      layout="horizontal"
      disabled={!isAdminUser}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      scrollToFirstError
      autoComplete="off"
    >
      <StyledEmailWrapper>
        <Form.Item
          name={'emailArray'}
          label={
            <Tooltip
              title="Specify the email addresses that will receive alert emails."
              placement="topLeft"
            >
              Email
            </Tooltip>
          }
          colon={false}
          rules={[
            {
              required: true,
              validator: (_, emailArray) => validateEmailArray(emailArray),
            },
          ]}
        >
          <StyledSelect
            placeholder="john@email.com"
            mode="tags"
            allowClear
            //disabled={!!editSubscription.emailId}
            showSearch={emailArrayWatch?.length >= MAX_COUNT}
            options={getEmailOptions()}
            optionFilterProp="label"
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? '')
                .toLowerCase()
                .localeCompare((optionB?.label ?? '').toLowerCase())
            }
          />
        </Form.Item>
        {!editSubscription.emailId && (
          <StyledInfo>
            <InfoCircleOutlined style={{ color: '#0046ff', marginRight: '3px' }} />
            Maximum {MAX_COUNT} allowed
          </StyledInfo>
        )}
      </StyledEmailWrapper>

      <EditMetricCategoryPanels form={form} categoryMap={categoryMap} searchProps={searchProps} />
    </Form>
  );
};

export default SubscriptionForm;
